import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Collapse, Stack, styled } from "~/common/mui";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Add, ExpandLess } from "@mui/icons-material";
import type { ProductLineViewModel } from "~/features/cart/cartV2/models/ProductTableViewModel";

interface CartEntryDetailsProps {
  initiallyExpanded?: boolean;
  canCopyLine: boolean;
  copySourcePackEntryId: number | null;
  lines: ProductLineViewModel[];
  renderItem: (line: ProductLineViewModel) => ReactNode;
  editMode: boolean;
  onCopyLine: (packEntryId: number) => void;
}

export const CartEntryDetails = ({
  initiallyExpanded,
  canCopyLine,
  copySourcePackEntryId,
  lines,
  renderItem,
  editMode,
  onCopyLine,
}: CartEntryDetailsProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(initiallyExpanded ?? false);

  return (
    <DetailsRoot $expanded={expanded}>
      <Box sx={{ p: 1, borderTop: 1, borderColor: "divider" }}>
        <Button
          variant="text"
          startIcon={expanded ? <ExpandLess /> : <ExpandMoreIcon />}
          color="textPrimary"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {`${expanded ? t("common.hide") : t("common.show")} ${t("cartPage.summary.productLines")}`}
        </Button>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 1 }}>
          <Stack spacing={2}>{lines.map((line) => renderItem(line))}</Stack>
        </Box>
        {editMode && (
          <Box sx={{ p: 2 }}>
            <Button
              onClick={() => {
                if (copySourcePackEntryId !== null) {
                  onCopyLine(copySourcePackEntryId);
                }
              }}
              startIcon={<Add />}
              variant="outlined"
              disabled={!canCopyLine || copySourcePackEntryId === null}
            >
              {t("cartPage.table.copyRow")}
            </Button>
          </Box>
        )}
      </Collapse>
    </DetailsRoot>
  );
};

const DetailsRoot = styled(Box)<{ $expanded: boolean }>(({ theme, $expanded }) => ({
  background: $expanded ? theme.palette.surfaceContainerLow?.main : theme.palette.common.white,
  transition: `background ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
}));
